// Workspace downloads (pure): chapters, transcript as text / SRT / WebVTT, chats and everything as
// Markdown. Transcript segments come from the exportTranscript callable: [{ t: seconds, text, visual }].

const pad = (n, w = 2) => String(n).padStart(w, '0');

export function clock(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

const cueTime = (sec, sep) => {
  const ms = Math.round(Math.max(0, sec) * 1000);
  return `${pad(Math.floor(ms / 3600000))}:${pad(Math.floor(ms / 60000) % 60)}:${pad(Math.floor(ms / 1000) % 60)}${sep}${pad(ms % 1000, 3)}`;
};

// "0:00 - Intro" lines (how chapters are stored) → "0:00 Intro", the format YouTube reads
export function chaptersText(timestamps) {
  return (timestamps || '').split('\n').map(l => l.trim()).filter(Boolean)
    .map(l => l.replace(/^(\d+(?::\d{2}){1,2})\s+-\s+/, '$1 '))
    .join('\n');
}

// Spoken lines only, consecutive pieces with the same start merged, each ending where the next starts
export function cues(segments, duration = 0) {
  const spoken = [];
  (segments || []).filter(s => !s.visual && (s.text || '').trim()).forEach(s => {
    const last = spoken[spoken.length - 1];
    if (last && last.t === s.t) last.text += ` ${s.text.trim()}`;
    else spoken.push({ t: s.t, text: s.text.trim() });
  });
  return spoken.map((s, i) => {
    const next = spoken[i + 1];
    const fallbackEnd = duration > s.t ? Math.min(duration, s.t + 6) : s.t + 4;
    const end = next ? Math.min(next.t, s.t + 10) : fallbackEnd;
    return { start: s.t, end: end > s.t ? end : s.t + 1, text: s.text };
  });
}

export const toSrt = (segments, duration) => cues(segments, duration)
  .map((c, i) => `${i + 1}\n${cueTime(c.start, ',')} --> ${cueTime(c.end, ',')}\n${c.text}\n`)
  .join('\n');

export const toVtt = (segments, duration) => `WEBVTT\n\n${cues(segments, duration)
  .map(c => `${cueTime(c.start, '.')} --> ${cueTime(c.end, '.')}\n${c.text}\n`)
  .join('\n')}`;

export function transcriptText(segments, { visual = true } = {}) {
  return (segments || [])
    .filter(s => visual || !s.visual)
    .map(s => `[${clock(s.t)}] ${s.visual ? `(On screen: ${s.text})` : s.text}`)
    .join('\n');
}

// Chat threads as built by chatModel.buildThreads (finished answers only)
export function chatMarkdown(threads) {
  return (threads || [])
    .map(t => ({ ...t, turns: (t.turns || []).filter(turn => turn.status === 'done' && (turn.answer || '').trim()) }))
    .filter(t => t.turns.length)
    .map(t => [`## ${t.name || 'Chat'}`, ...t.turns.map(turn => `**You:** ${turn.prompt}\n\n${turn.answer.trim()}`)].join('\n\n'))
    .join('\n\n---\n\n');
}

export function everythingMarkdown({ title, url, timestamps, threads, segments }) {
  const parts = [`# ${title || 'Workspace'}`];
  if (url) parts.push(url);
  const chapters = chaptersText(timestamps);
  if (chapters) parts.push(`## Chapters\n\n${chapters}`);
  const chats = chatMarkdown(threads);
  if (chats) parts.push(`# Chats\n\n${chats}`);
  if (segments && segments.length) parts.push(`## Transcript\n\n${transcriptText(segments)}`);
  parts.push('_Exported from Bumpups (bumpups.com)_');
  return `${parts.join('\n\n')}\n`;
}

export function fileName(title, ext) {
  const base = (title || 'bumpups').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'bumpups';
  return `${base}.${ext}`;
}
